import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { useToast } from '../ui/use-toast';
import { isoToDatetimeLocal, datetimeLocalToIso } from '../../utils/datetime-local';
import { scheduleEventsService } from '../../services/schedule-events';
import { WEEKDAYS, type ScheduleEventPayload, type ScheduleOccurrence, type Weekday } from '../../types/schedule-event';

/** Fixed palette shown as swatches — the app has no free color picker. */
const EVENT_COLORS = [
  '#EF4444', '#F97316', '#EAB308', '#22C55E', '#14B8A6',
  '#3B82F6', '#8B5CF6', '#EC4899', '#78716C', '#0EA5E9',
];

interface ScheduleEventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  /** When provided, the dialog edits this occurrence's event; otherwise it creates a new one. */
  editing?: ScheduleOccurrence | null;
  onSaved: () => void;
  onDeleted: () => void;
}

export function ScheduleEventFormDialog({
  open,
  onOpenChange,
  gymSlug,
  editing,
  onSaved,
  onDeleted,
}: ScheduleEventFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(EVENT_COLORS[5]);
  const [recurring, setRecurring] = useState(false);
  // Single mode: full datetime-local values.
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  // Recurring mode: the anchor date, daily time window, weekdays and end date.
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('19:00');
  const [weekdays, setWeekdays] = useState<Weekday[]>([]);
  const [untilDate, setUntilDate] = useState('');
  const [capacity, setCapacity] = useState('');
  const [staffOnly, setStaffOnly] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [notifyStaff, setNotifyStaff] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset the form whenever the dialog opens (prefilling in edit mode from the
  // event-level anchor times, not the tapped occurrence's).
  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title ?? '');
    setDescription(editing?.description ?? '');
    setColor(editing?.color ?? EVENT_COLORS[5]);
    setRecurring(editing?.recurring ?? false);
    const anchorStart = editing?.event_starts_at;
    const anchorEnd = editing?.event_ends_at;
    setStartsAt(isoToDatetimeLocal(anchorStart));
    setEndsAt(isoToDatetimeLocal(anchorEnd));
    setStartDate(anchorStart ? format(parseISO(anchorStart), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'));
    setStartTime(anchorStart ? format(parseISO(anchorStart), 'HH:mm') : '18:00');
    setEndTime(anchorEnd ? format(parseISO(anchorEnd), 'HH:mm') : '19:00');
    setWeekdays(editing?.recurrence?.byday ?? []);
    setUntilDate(editing?.recurrence_ends_on ?? '');
    setCapacity(editing?.max_participants != null ? String(editing.max_participants) : '');
    setStaffOnly(editing?.staff_only_registration ?? false);
    setRequiresConfirmation(editing?.requires_confirmation ?? false);
    setNotifyStaff(editing?.notify_staff_on_registration ?? false);
    setConfirmDelete(false);
  }, [open, editing]);

  const handleStartsAtChange = (value: string) => {
    setStartsAt(value);
    setEndsAt(prev => (value && (!prev || prev < value) ? value : prev));
  };

  const toggleWeekday = (day: Weekday) => {
    setWeekdays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  };

  const buildPayload = (): ScheduleEventPayload | null => {
    const base: ScheduleEventPayload = {
      title: title.trim(),
      description: description.trim() || null,
      color,
      max_participants: capacity ? parseInt(capacity, 10) : null,
      staff_only_registration: staffOnly,
      requires_confirmation: requiresConfirmation,
      notify_staff_on_registration: notifyStaff,
    };

    if (!recurring) {
      if (!startsAt || !endsAt) return null;
      if (endsAt <= startsAt) {
        toast({ title: t('schedule.form.endBeforeStartError'), variant: 'destructive' });
        return null;
      }
      return {
        ...base,
        starts_at: datetimeLocalToIso(startsAt) ?? undefined,
        ends_at: datetimeLocalToIso(endsAt) ?? undefined,
        recurrence: null,
        recurrence_ends_on: null,
      };
    }

    if (!startDate || !startTime || !endTime) return null;
    if (weekdays.length === 0) {
      toast({ title: t('schedule.form.noWeekdaysError'), variant: 'destructive' });
      return null;
    }
    if (!untilDate) {
      toast({ title: t('schedule.form.untilRequiredError'), variant: 'destructive' });
      return null;
    }
    if (endTime <= startTime) {
      toast({ title: t('schedule.form.endBeforeStartError'), variant: 'destructive' });
      return null;
    }
    // Keep the weekday pills in mo..su order regardless of tap order.
    const byday = WEEKDAYS.filter(day => weekdays.includes(day));
    return {
      ...base,
      starts_at: datetimeLocalToIso(`${startDate}T${startTime}`) ?? undefined,
      ends_at: datetimeLocalToIso(`${startDate}T${endTime}`) ?? undefined,
      recurrence: { freq: 'weekly', interval: 1, byday },
      recurrence_ends_on: untilDate,
    };
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    try {
      if (editing) {
        await scheduleEventsService.update(gymSlug, editing.event_id, payload);
      } else {
        await scheduleEventsService.create(gymSlug, payload);
      }
      toast({ title: editing ? t('schedule.form.updateSuccess') : t('schedule.form.createSuccess') });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    setDeleting(true);
    try {
      await scheduleEventsService.remove(gymSlug, editing.event_id);
      toast({ title: t('schedule.form.deleteSuccess') });
      onDeleted();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('schedule.form.editTitle') : t('schedule.form.createTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="schedule-event-title">{t('schedule.form.title')} *</Label>
            <Input
              id="schedule-event-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={120}
              placeholder={t('schedule.form.titlePlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="schedule-event-description">{t('schedule.form.description')}</Label>
            <Textarea
              id="schedule-event-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('schedule.form.color')}</Label>
            <div className="flex flex-wrap gap-2">
              {EVENT_COLORS.map(hex => (
                <button
                  key={hex}
                  type="button"
                  className={`h-7 w-7 rounded-full border transition-shadow ${
                    color === hex ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : ''
                  }`}
                  style={{ backgroundColor: hex }}
                  onClick={() => setColor(hex)}
                  aria-label={hex}
                  aria-pressed={color === hex}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="schedule-event-recurring">{t('schedule.form.recurring')}</Label>
            <Switch id="schedule-event-recurring" checked={recurring} onCheckedChange={setRecurring} />
          </div>

          {!recurring ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="schedule-event-starts-at">{t('schedule.form.startsAt')} *</Label>
                <Input
                  id="schedule-event-starts-at"
                  type="datetime-local"
                  value={startsAt}
                  onChange={e => handleStartsAtChange(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schedule-event-ends-at">{t('schedule.form.endsAt')} *</Label>
                <Input
                  id="schedule-event-ends-at"
                  type="datetime-local"
                  value={endsAt}
                  min={startsAt || undefined}
                  onChange={e => setEndsAt(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>{t('schedule.form.weekdays')} *</Label>
                <div className="flex gap-1.5">
                  {WEEKDAYS.map(day => (
                    <button
                      key={day}
                      type="button"
                      className={`h-8 w-8 rounded-full text-xs font-medium border transition-colors ${
                        weekdays.includes(day)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground hover:bg-accent'
                      }`}
                      onClick={() => toggleWeekday(day)}
                      aria-pressed={weekdays.includes(day)}
                    >
                      {t(`schedule.form.weekdayShort.${day}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="schedule-event-start-time">{t('schedule.form.startTime')} *</Label>
                  <Input
                    id="schedule-event-start-time"
                    type="time"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="schedule-event-end-time">{t('schedule.form.endTime')} *</Label>
                  <Input
                    id="schedule-event-end-time"
                    type="time"
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="schedule-event-start-date">{t('schedule.form.startDate')} *</Label>
                  <Input
                    id="schedule-event-start-date"
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="schedule-event-until-date">{t('schedule.form.untilDate')} *</Label>
                  <Input
                    id="schedule-event-until-date"
                    type="date"
                    value={untilDate}
                    min={startDate || undefined}
                    onChange={e => setUntilDate(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="schedule-event-capacity">{t('schedule.form.capacity')}</Label>
            <Input
              id="schedule-event-capacity"
              type="number"
              min={1}
              value={capacity}
              onChange={e => setCapacity(e.target.value)}
              placeholder={t('schedule.form.capacityPlaceholder')}
            />
          </div>

          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="schedule-event-staff-only" className="font-normal">
                {t('schedule.flags.staffOnly')}
              </Label>
              <Switch id="schedule-event-staff-only" checked={staffOnly} onCheckedChange={setStaffOnly} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="schedule-event-requires-confirmation" className="font-normal">
                {t('schedule.flags.requiresConfirmation')}
              </Label>
              <Switch
                id="schedule-event-requires-confirmation"
                checked={requiresConfirmation}
                onCheckedChange={setRequiresConfirmation}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="schedule-event-notify-staff" className="font-normal">
                {t('schedule.flags.notifyStaff')}
              </Label>
              <Switch id="schedule-event-notify-staff" checked={notifyStaff} onCheckedChange={setNotifyStaff} />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {editing && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="sm:mr-auto"
              onClick={() => setConfirmDelete(true)}
              disabled={saving || deleting}
              aria-label={t('schedule.form.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving || deleting}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || deleting || !title.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('schedule.form.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('schedule.form.deleteConfirmBody', { title: editing?.title ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={e => {
                e.preventDefault();
                handleDelete();
              }}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('schedule.form.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
